import React, { useState } from 'react';
import { Input } from '@frontend/shadcn/components/ui/input';
import { OFFER_PAYOUT_TYPE } from '../../../types/globalTypes';
import { RuleCommission, StandaloneInputProps } from './types';

const StandaloneInputBase = ({
  initialValue,
  onChange,
  placeholder,
  prefix,
  suffix,
  max
}: StandaloneInputProps) => {
  const [localValue, setLocalValue] = useState(() => initialValue?.toString() ?? '');

  const handleBlur = () => {
    if (localValue) {
      onChange(Number(localValue));
    } else {
      onChange(0);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  return (
    <div className="relative w-full">
      {prefix && <span className="absolute left-3 top-[5px] text-grey-4">{prefix}</span>}
      <Input
        type="number"
        min={0}
        max={max}
        placeholder={placeholder}
        className={`w-full bg-primary-foreground ${prefix ? 'pl-8' : ''} ${suffix ? 'pr-8' : ''}`}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {suffix && <span className="absolute right-3 top-[5px] text-grey-4">{suffix}</span>}
    </div>
  );
};

export const StandaloneInput = React.memo(StandaloneInputBase);
StandaloneInput.displayName = 'StandaloneInput';

interface PayoutCommissionInputProps {
  payoutType: OFFER_PAYOUT_TYPE;
  commissionValue: RuleCommission;
  onCommissionChange: (value: number, isPercent: boolean) => void;
}

const PayoutCommissionInputBase = ({
  payoutType,
  commissionValue,
  onCommissionChange
}: PayoutCommissionInputProps) => {
  switch (payoutType) {
    case OFFER_PAYOUT_TYPE.SALE:
      return (
        <div className="relative w-full max-w-[229px]">
          <StandaloneInput
            initialValue={commissionValue.percentPayout}
            onChange={(value) => onCommissionChange(value, true)}
            placeholder="Commission Percentage"
            suffix="%"
            max={100}
          />
        </div>
      );
    case OFFER_PAYOUT_TYPE.CONVERSION:
    case OFFER_PAYOUT_TYPE.CLICK:
      return (
        <div className="relative w-full max-w-[229px]">
          <StandaloneInput
            initialValue={commissionValue.flatPayout}
            onChange={(value) => onCommissionChange(value, false)}
            placeholder="Commission Amount"
            prefix="$"
          />
        </div>
      );
    case OFFER_PAYOUT_TYPE.CONVERSION_AND_SALE:
      return (
        <div className="flex items-center gap-2 w-full max-w-[400px]">
          <div className="relative w-[180px]">
            <StandaloneInput
              initialValue={commissionValue.flatPayout}
              onChange={(value) => onCommissionChange(value, false)}
              placeholder="Flat Amount"
              prefix="$"
            />
          </div>
          <span>+</span>
          <div className="relative w-[180px]">
            <StandaloneInput
              initialValue={commissionValue.percentPayout}
              onChange={(value) => onCommissionChange(value, true)}
              placeholder="Percentage"
              suffix="%"
              max={100}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
};

export const PayoutCommissionInput = React.memo(PayoutCommissionInputBase);
PayoutCommissionInput.displayName = 'PayoutCommissionInput';
